import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ListingsHeader from './listings-header';
import ListingsFilter from './filters/listings-filter-bar';
import ListingsMain from './listings-main';
import styled from 'styled-components';
import MiqWarningIcon from '../../assets/images/svg/miq-warning.svg';
import { Listing } from '../../types/listing/listing';
import { MainMessaging, AlertMessaging, State } from '../../types/state';
import { mainMessageSelector } from '../../redux/selectors/system/main-message-selector';
import { alertMessageSelector } from '../../redux/selectors/system/alert-message-selector';
import MainMessage from '../common/messages/main-message';
import AlertMessage from '../common/messages/alert-message';
import { clearAlertMessage } from '../../redux/actions/system/set-alert-message';
import { getListingsPaged, resetPagedListings } from '../../redux/actions/pagedListings/load-listings-paged';
import { getAssignedListingsCount } from '../../redux/actions/miq/get-assigned-listings-count';
import { pagedListingsSelector } from '../../redux/selectors/pagedListings/paged-listings-selector';
import { skipSelector } from '../../redux/selectors/pagedListings/skip-selector';
import { takeSelector } from '../../redux/selectors/pagedListings/take-selector';
import { moreRecordsSelector } from '../../redux/selectors/pagedListings/more-records-selector';
import { FilterSetup, Filter } from '../../types/listing/filters';
import { filterSetupsSelector } from '../../redux/selectors/pagedListings/filter-setups-selector';
import { extractFilters } from '../../utils/extract-filters';
import { searchTextSelector } from '../../redux/selectors/pagedListings/search-text-selector';
import { filtersChanged } from '../../redux/actions/pagedListings/change-filters';
import { clearMIQData } from '../../redux/actions/miq/clear-miq-data';
import { setCancelAction } from '../../redux/actions/system/set-cancel-action';
import { assignedListingsSelector } from '../../redux/selectors/pagedListings/assigned-listings-selector';
import { setSaveAction } from '../../redux/actions/system/set-save-action';
import { setMIQAddEditRecord } from '../../redux/actions/miq/select-miq-record-add-edit'
import { homeSiteIdSelector } from '../../redux/selectors/system/siteid-selector';
import { regionIdSelector } from '../../redux/selectors/system/config-region-id-selector';
import { changeFilters } from '../../redux/actions/pagedListings/change-filters';
import { refreshAllContacts } from '../../redux/actions/contacts/refresh-all-contacts';
import { mainLoaderSelector } from '../../redux/selectors/system/main-loader-selector';
import { countOutOfSyncListings } from '../../api/glQueries';
import { postData } from '../../api/glAxios';
import { configSelector } from '../../redux/selectors/system/config-selector';
import { isMiqSyncEnabled } from '../../utils/config/miq-sync-enabled';

// handles miqSyncFlag arriving as a boolean, string, or numeric value
const isOutOfSync = (listing: Listing): boolean => {
    return listing.miqSyncFlag === true
        || String(listing.miqSyncFlag).toLowerCase() === 'true'
        || listing.miqSyncFlag === (1 as any);
};

const ListingsContainer: FC = () => {

    const dispatch = useDispatch();
    const [showMiqNotice, setShowMiqNotice] = useState<boolean>(false);
    const [showOnlyOutOfSync, setShowOnlyOutOfSync] = useState<boolean>(false);
    const [outOfSyncListingCount, setOutOfSyncListingCount] = useState<number | null>(null);

    const mainMessage: MainMessaging = useSelector(mainLoaderSelector);
    const alertMessage: AlertMessaging = useSelector(alertMessageSelector);

    const filterSetups: FilterSetup[] = useSelector(filterSetupsSelector);
    const searchText: string = useSelector(searchTextSelector);
    const listings: Listing[] = useSelector(pagedListingsSelector);
    const assignedListings: Listing[] = useSelector(assignedListingsSelector);
    const skip: number = useSelector(skipSelector);
    const take: number = useSelector(takeSelector);
    const moreRecordsAvailable: boolean = useSelector(moreRecordsSelector);
    const homeSiteID = useSelector(homeSiteIdSelector);
    const selectedRegionId = useSelector(regionIdSelector);
    const miqSyncEnabled = isMiqSyncEnabled(useSelector(configSelector));
    const storedFilters = useSelector((state: State) => state.pagedListings.filters as unknown as FilterSetup[]);
    const firstOutOfSyncListing = listings.find(isOutOfSync);
    const pendingMiqListingId = firstOutOfSyncListing ? String(firstOutOfSyncListing.id) : undefined;
    const loadedOutOfSyncListingCount = listings.filter(isOutOfSync).length
        + assignedListings.filter(isOutOfSync).length;
    const displayedOutOfSyncListingCount = outOfSyncListingCount === null
        ? loadedOutOfSyncListingCount
        : outOfSyncListingCount;
    const outOfSyncListingLabel = displayedOutOfSyncListingCount === 1 ? 'listing has' : 'listings have';

    const filterChangeHandler = (changedFilters: FilterSetup[]) => {
        setShowOnlyOutOfSync(miqSyncEnabled && changedFilters.some(filter => filter.uid === 'outofsync' && filter.selected));
        dispatch(filtersChanged(changedFilters));
    }

    const toggleOutOfSyncListings = () => {
        const changedFilters = filterSetups.map((filter: FilterSetup) => ({
            ...filter,
            selected: showOnlyOutOfSync ? filter.uid === 'viewall' : filter.uid === 'outofsync'
        }));
        filterChangeHandler(changedFilters);
    }

    useEffect(() => {
        clearAlertMessage(dispatch);
        dispatch(clearMIQData()); // clear out any lingering MIQ data when we hit the listings page
        dispatch(setCancelAction({goto: "/"})); // ensure any cancel action in a subsequent page returns to this page
        dispatch(setSaveAction({}));
        dispatch(getAssignedListingsCount());
        getNextListings();
        dispatch(setMIQAddEditRecord(null)) // clear out imported miq record
    }, []);

    useEffect(() => {
        if(!searchText){
            dispatch(changeFilters(resetFilters(filterSetups)));
            resetPagedListings(dispatch, true);
            dispatch(refreshAllContacts());
        }
    }, [homeSiteID]);

    useEffect(() => {
        latestRegionCountRequestRef.current += 1;
        setShowMiqNotice(false);
        setOutOfSyncListingCount(null);
        setShowOnlyOutOfSync(false);
        if (!miqSyncEnabled && storedFilters.some(filter => filter.uid === 'outofsync' && filter.selected)) {
            dispatch(changeFilters(filterSetups));
        }
        if (miqSyncEnabled && selectedRegionId) {
            loadOutOfSyncListingCount(selectedRegionId);
        }
        return () => { latestRegionCountRequestRef.current += 1; };
    }, [selectedRegionId, miqSyncEnabled]);

    const latestRegionCountRequestRef = useRef(0);

    const loadOutOfSyncListingCount = (regionId: string) => {
        const requestId = ++latestRegionCountRequestRef.current;
        postData(countOutOfSyncListings(regionId)).then((response: any) => {
            if (latestRegionCountRequestRef.current !== requestId) {
                return;
            }
            if (response && response.data && typeof response.data.count === 'number') {
                const count = response.data.count;
                setOutOfSyncListingCount(count);
                setShowMiqNotice(count > 0);
            }
        }).catch((error: any) => {
            if (latestRegionCountRequestRef.current !== requestId) {
                return;
            }
            setShowMiqNotice(false);
            console.error('Unable to load out-of-sync listing count', error);
        });
    };

    const resetFilters = (filters:FilterSetup[]) : FilterSetup[] => {
        const updatedFilters:FilterSetup[] = [];
    
        filters.forEach((setup:FilterSetup) => {
            if(setup.uid === 'viewall'){
                setup.selected = true;
            }
            else{
                setup.selected = false;
            }
            updatedFilters.push(setup);
        });
    
        return updatedFilters;
    }

    const getAssignedCount = () => {
        dispatch(getAssignedListingsCount());
    }

    const getNextListings = () => {
        const filters: Filter[] = extractFilters(filterSetups, searchText);
        if (listings && listings.length > 0) {
            dispatch(getListingsPaged(skip, take, filters));
        } else {
            dispatch(getListingsPaged(0, take, filters));
        }
    }

    return (
        <>
            <ListingsHeader showSearch={true} />
            {alertMessage && alertMessage.show && <AlertMessage alertMessage={alertMessage} />}
            <ListingsMainContainer style={{ marginTop: '20px', marginBottom: '260px' }}>
                {miqSyncEnabled && showMiqNotice && !showOnlyOutOfSync &&
                    <MiqNotice role="status">
                        <WarningIcon src={MiqWarningIcon} alt="" aria-hidden="true" />
                        <MiqNoticeText>{displayedOutOfSyncListingCount} {outOfSyncListingLabel} pending updates from MIQ. Review the changes and decide whether to apply or dismiss them.</MiqNoticeText>
                        <NoticeAction type="button" onClick={toggleOutOfSyncListings}>
                            {showOnlyOutOfSync ? 'View All Listings' : 'View Out of Sync Listings'}
                        </NoticeAction>
                        <CloseNotice type="button" aria-label="Dismiss MIQ update notice" onClick={() => setShowMiqNotice(false)}>×</CloseNotice>
                    </MiqNotice>}
                {
                    <ListingsFilter setups={filterSetups} changeFilters={filterChangeHandler} />
                }
                {
                    mainMessage.show && <MainMessage mainMessage={mainMessage} />
                }
                {
                    !mainMessage.show && <ListingsMain normalListings={listings} assignedListings={assignedListings}
                                                loadListingsHandler={getNextListings} moreRecordsAvailable={moreRecordsAvailable}
                                                pendingMiqListingId={pendingMiqListingId} />
                }
            </ListingsMainContainer>
        </>
    );

};

const ListingsMainContainer = styled.div`
    font-family: ${props => (props.theme.font ? props.theme.font.primary : 'sans-serif')};
    max-width: ${props => props.theme.container.maxWidth};
    width: ${props => props.theme.container.width};
    margin:0 auto;
`;

const MiqNotice = styled.div`
    min-height: 64px;
    margin: 0 0 28px;
    padding: 16px;
    border: 1px solid #ff9742;
    border-radius: 2px;
    background: #ffecd9;
    background-clip: padding-box;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 12px;
    box-sizing: border-box;
    color: #3f3f3f;
`;

const WarningIcon = styled.img`
    width: 21px;
    height: 18px;
    flex: 0 0 auto;
`;

const MiqNoticeText = styled.span`
    flex: 1;
    margin-right: 24px;
    font-family: 'Calibre-R', helvetica, arial, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    vertical-align: middle;
`;

const NoticeAction = styled.button`
    min-width: 183px;
    min-height: 32px;
    border: 1px solid #003f2d;
    border-radius: 8px;
    background: #fff;
    color: #003f2d;
    height: 32px;
    padding: 0 16px;
    cursor: pointer;
    font-family: ${props => props.theme && props.theme.font ? props.theme.font.bold : 'helvetica, arial, sans-serif'};
    font-size: 13px;
    line-height: 30px;
    white-space: nowrap;

    &:focus-visible {
        outline: 2px solid #003f2d;
        outline-offset: 2px;
    }
`;

const CloseNotice = styled.button`
    border: 0;
    background: transparent;
    color: #111;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    font-size: 28px;
    line-height: 1;
`;

export default ListingsContainer;